import moment from "moment";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "../../../types/income";
import { HousingAssistanceSlice } from "../HousingAssistanceSlice";
import { Section } from "views/applications-id-report/components/Section";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  getIncomeJobName,
  getIncomeOriginalNetAndGrossDollars,
  incomeTypeMap
} from "../../../utils";
import { has } from "shared/utils";
import { Show } from "shared/components";
import { SummaryUpdated } from "../../../components/SummaryUpdated";
import { ApplicationStatus, VerifyMethodType } from "shared/types";
import { formatMonths } from "../../../helpers";
import { IncomeSourceStatusBadge } from "../../../components/IncomeSourceStatusBadge";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Loading03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IncomeSliceHeader } from "./components/Header";
import { SelfReportedStats } from "./components/SelfReportedStats";
import { VerifiedItem } from "../../../types/verified";
import { BankStatementItem } from "../../../types/verified/bank-statment";
import { VerificationSettings } from "types/landlord/reports";
import { EditIncomeModalData } from "../../../types";
import { Applicant } from "types/application";
import { VerifiedIncomeDetails } from "./components/VerifiedIncomeDetails";

export const IncomeSlice = ({
  income,
  applicationStatus,
  applicationId,
  additionalData,
  settings,
  setShowReverifyIncomeModal,
  setShowEditIncomeModal,
  isManualApplicant,
  applicant
}: {
  income: CombinedIncome;
  applicationStatus: ApplicationStatus;
  applicationId?: string | number;
  additionalData?: VerificationSpecificAdditionaData | null;
  settings?: VerificationSettings;
  setShowReverifyIncomeModal: Dispatch<SetStateAction<number | false>>;
  setShowEditIncomeModal: Dispatch<SetStateAction<false | EditIncomeModalData>>;
  isManualApplicant: boolean;
  applicant: Applicant;
}) => {
  const [currentIncomeIndex, setCurrentIncomeIndex] = useState(0);

  const diff = moment().diff(moment(income.start_date), "months");

  const incomeBankStatementsItems = useMemo(() => {
    if (
      !isManualApplicant ||
      income.verified_source_type !== VerifyMethodType.BankStatement
    )
      return undefined;

    const incomeBankStatements =
      additionalData?.bank_statement?.allBankStatements || {};

    return income.income_sources_ids?.reduce(
      (acc, income_source_id) => {
        const items = incomeBankStatements[income_source_id].items;
        if (items.length)
          acc.push(
            items.map((item) => ({
              id: item.id,
              income_source_id: income_source_id,
              income_id: income.id,
              created_at: income.created_at,
              updated_at: income.updated_at,
              is_archived: item.is_archived || false,
              reverify_log: item.reverify_log,
              income_source_type: "bank_statement" as const,
              data: item.data
            })) as BankStatementItem[]
          );

        return acc;
      },
      [] as Array<Array<BankStatementItem>>
    );
  }, [income, additionalData, isManualApplicant]);

  const { groupedIncomeItems } = useMemo(() => {
    const incomeItems = [...income.items].reverse();

    const groupedItems =
      incomeBankStatementsItems ||
      incomeItems.reduce(
        (acc, item) => {
          if (item.income_source_type === VerifyMethodType.BankStatement) {
            const groupedBankStatement = acc.find((incomeItem) =>
              Array.isArray(incomeItem)
            );
            if (groupedBankStatement) groupedBankStatement.push(item);
            else acc.push([item]);

            return acc;
          }

          acc.push(item);
          return acc;
        },
        [] as (VerifiedItem | BankStatementItem[])[]
      );

    return {
      incomeItems,
      groupedIncomeItems: groupedItems
    };
  }, [income.items, income.type, settings]);

  const currentItem = groupedIncomeItems[currentIncomeIndex] as
    | VerifiedItem
    | VerifiedItem[]
    | undefined;

  const { originalNetMonthlyIncomeDollars, originalGrossMonthlyIncomeDollars } =
    getIncomeOriginalNetAndGrossDollars(income, currentItem);

  if (income.type === "housing_assistance")
    return (
      <HousingAssistanceSlice
        amountCents={income.amount_cents}
        assets={income.assets}
        incomeId={income.id}
        applicant={applicant}
        verificationId={income.verification_id}
      />
    );

  return (
    <Section
      id={`combined-income-section-${income.id}`}
      maxWidth="1000px"
      sectionTitle={
        <p className="flex items-center gap-2 text-content-400 font-medium text-sm">
          {income.additional_data ? (
            <>
              {getIncomeJobName(income)}
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
            </>
          ) : (
            ""
          )}
          {incomeTypeMap[income.type] || income.type}
          {income.status === "processing" ? null : (
            <>
              <span className="block size-1.5 shrink-0 bg-content-100 rounded-full" />
              <IncomeSourceStatusBadge income={income} />
            </>
          )}
        </p>
      }
      rightElement={
        <p className="text-content-400 text-sm font-medium">
          {income.type === "offer_letter"
            ? moment(income.start_date).format("ll")
            : `${moment(income.start_date).format("ll")} - Present (${formatMonths(diff)})`}
        </p>
      }
    >
      <IncomeSliceHeader
        income={income}
        currentItem={currentItem}
        setShowEditIncomeModal={setShowEditIncomeModal}
      />
      <Show
        if={
          has(income.additional_data?.overrides?.gross_monthly_income) ||
          has(income.additional_data?.overrides?.net_monthly_income)
        }
      >
        <SummaryUpdated
          className="mb-10"
          changes={income.additional_data?.overrides}
          original={{
            gross: originalNetMonthlyIncomeDollars,
            net: originalGrossMonthlyIncomeDollars
          }}
        />
      </Show>

      <SelfReportedStats income={income} />

      {income.status === "processing" ? (
        <div className="flex flex-col items-center justify-center min-h-[250px] max-w-[468px] w-full mx-auto">
          <div className="size-16 shrink-0 bg-warning-50 rounded-full flex items-center justify-center mb-6 mx-auto">
            <HugeiconsIcon
              icon={Loading03Icon}
              size={24}
              className="text-warning-600"
            />
          </div>

          <h2 className="text-base font-bold text-content-500 mb-2 text-center">
            Verifying income source with documents...
          </h2>

          <p className="text-center text-sm font-medium text-content-300">
            Verification may take up to an hour
          </p>
        </div>
      ) : (
        <VerifiedIncomeDetails
          groupedIncomeItems={groupedIncomeItems}
          income={income}
          currentIncomeIndex={currentIncomeIndex}
          setCurrentIncomeIndex={setCurrentIncomeIndex}
          currentItem={currentItem}
          applicationId={applicationId}
          applicationStatus={applicationStatus}
          setShowReverifyIncomeModal={setShowReverifyIncomeModal}
          isManualApplicant={isManualApplicant}
          additionalData={additionalData}
        />
      )}
    </Section>
  );
};
